import React, { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { PRODUCTION_PREFIX } from "../../config";
import {
  pagePathFromPathname,
  parseProjectIdFromPathname,
  PATHS,
  projectPath,
} from "../../config/paths";
import { useProject } from "../../contexts/ProjectContext";
import LoadingSpinner from "../LoadingSpinner";

/** Keep the selected project in sync with `/:projectId/...` in the URL. */
export function ProjectRouteSync() {
  const { projectId, setProjectId, projects, ready } = useProject();
  const location = useLocation();
  const navigate = useNavigate();
  const urlProjectId = parseProjectIdFromPathname(
    location.pathname,
    PRODUCTION_PREFIX,
  );

  useEffect(() => {
    if (!ready || urlProjectId == null) {
      return;
    }
    if (projects.some((row) => row.id === urlProjectId)) {
      if (urlProjectId !== projectId) {
        setProjectId(urlProjectId);
      }
      return;
    }
    if (projectId != null && projects.some((row) => row.id === projectId)) {
      const page = pagePathFromPathname(location.pathname, PRODUCTION_PREFIX);
      navigate(`${projectPath(projectId, page)}${location.search}`, {
        replace: true,
      });
    }
  }, [
    ready,
    urlProjectId,
    projectId,
    projects,
    setProjectId,
    navigate,
    location.pathname,
    location.search,
  ]);

  return null;
}

/** Upgrade an old unscoped page (`/property`) to `/:projectId/property`. */
export function LegacyProjectPageRedirect({
  pagePath,
  targetPagePath,
}: {
  pagePath: string;
  targetPagePath?: string;
}) {
  const { projectId, ready } = useProject();
  const location = useLocation();
  if (!ready) {
    return <LoadingSpinner />;
  }
  if (projectId == null) {
    return <Navigate to={PATHS.MAIN} replace />;
  }
  const destination = targetPagePath ?? pagePath;
  return (
    <Navigate
      to={`${projectPath(projectId, destination)}${location.search}${location.hash}`}
      replace
    />
  );
}

/** `/:projectId/contacts` → `/:projectId/property` (bookmark compatibility). */
export function LegacyProjectScopedContactsRedirect() {
  const location = useLocation();
  const projectId = parseProjectIdFromPathname(
    location.pathname,
    PRODUCTION_PREFIX,
  );
  if (projectId == null) {
    return <Navigate to={PATHS.MAIN} replace />;
  }
  return (
    <Navigate
      to={`${projectPath(projectId, PATHS.PROPERTY)}${location.search}${location.hash}`}
      replace
    />
  );
}
